import React, { useCallback, useEffect, useState } from 'react';
import TableHeader from '../../shared/components/tableHeader/TableHeader';
import ContractModal from '../contracts/ContractModal';
import {
    DEFAULT_TRIAL_DAYS, MAX_TRIAL_DAYS, formatDate, isOpen, isoDatePlusDays, statusBadge, validityText,
    inputClass, labelClass, primaryButton, actionLink, dangerLink,
} from '../contracts/contractLabels';
import {
    getLoanerUnits, getTrials, startTrial, extendTrial, endTrial, changeProspectPassword, errorMessage,
} from '../../services/contracts/contractsService';
import { getTimeZones } from '../../services/timeZones/timeZonesService';
import { useUser } from '../../context/UserContext';

const PASSWORD_HINT = 'Mínimo 8 caracteres, con mayúscula, minúscula, número y un carácter especial (@ # $ % ^ & + = * ! ? .), sin espacios.';

const emptyLoan = () => ({
    existing: false, sensorId: '', days: DEFAULT_TRIAL_DAYS, notes: '', plan: 'FULL',
    email: '', firstName: '', lastName: '', phone: '', address: '', password: '', timeZoneId: '',
});

const addDays = (isoDate, days) => {
    const [y, m, d] = isoDate.split('-').map(Number);
    const date = new Date(y, m - 1, d + days);
    const pad = (n) => String(n).padStart(2, '0');
    return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
};

// Préstamos de equipos a prospectos. El vendedor ve el inventario completo pero solo
// gestiona sus propios préstamos; el administrador los ve y gestiona todos.
const LoansView = () => {
    const { user } = useUser();
    const isAdmin = !!(user && user.authorities.includes('ROLE_ADMIN'));

    const [units, setUnits] = useState([]);
    const [trials, setTrials] = useState([]);
    const [timeZones, setTimeZones] = useState([]);
    const [showHistory, setShowHistory] = useState(false);
    const [modal, setModal] = useState(null); // { kind: 'new' | 'extend' | 'password', trial? }
    const [loan, setLoan] = useState(emptyLoan());
    const [extendDays, setExtendDays] = useState(5);
    const [newPassword, setNewPassword] = useState('');
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState(null);

    const load = useCallback(async () => {
        try {
            const [u, t] = await Promise.all([getLoanerUnits(), getTrials()]);
            setUnits(u);
            setTrials(t);
        } catch (e) {
            console.error('Error al cargar los préstamos: ', e);
        }
    }, []);

    useEffect(() => { load(); }, [load]);

    useEffect(() => {
        getTimeZones(1, 50).then(setTimeZones).catch((e) => console.error(e));
    }, []);

    const available = units.filter((u) => u.available);
    const visibleTrials = showHistory ? trials : trials.filter(isOpen);

    const closeModal = () => { setModal(null); setError(null); };

    const submit = async (action, successMessage) => {
        setBusy(true);
        setError(null);
        try {
            await action();
            closeModal();
            alert(successMessage);
            load();
        } catch (e) {
            setError(errorMessage(e));
        } finally {
            setBusy(false);
        }
    };

    const handleNewLoan = () => submit(() => startTrial({
        sensorId: loan.sensorId,
        days: Number(loan.days),
        notes: loan.notes,
        email: loan.email.trim().toLowerCase(),
        ...(loan.existing ? {} : {
            plan: loan.plan,
            firstName: loan.firstName, lastName: loan.lastName, phone: loan.phone, address: loan.address,
            password: loan.password, timeZoneId: loan.timeZoneId ? Number(loan.timeZoneId) : null,
        }),
    }), `Préstamo creado. ${loan.existing ? 'El prospecto' : 'El prospecto puede entrar con su correo y la contraseña que definiste y'} verá el sensor hasta el ${formatDate(isoDatePlusDays(Number(loan.days)))}.`);

    const handleEnd = async (trial) => {
        if (!window.confirm(`¿Terminar el préstamo del equipo ${trial.sensorId}? El prospecto pierde el acceso y el equipo queda disponible.`)) return;
        try {
            await endTrial(trial.id);
            load();
        } catch (e) {
            alert(errorMessage(e));
        }
    };

    const maxDays = isAdmin ? undefined : MAX_TRIAL_DAYS;
    const field = (name) => ({ value: loan[name], onChange: (e) => setLoan({ ...loan, [name]: e.target.value }) });

    return (
        <div className="flex flex-col p-3 space-y-6">
            <div className="flex max-sm:flex-col items-center justify-between gap-2">
                <h1 className="text-3xl font-semibold text-blue-600">Préstamos</h1>
                <button type="button" className={primaryButton} disabled={available.length === 0}
                    title={available.length === 0 ? 'No hay equipos disponibles' : ''}
                    onClick={() => { setLoan(emptyLoan()); setError(null); setModal({ kind: 'new' }); }}>
                    <span className="material-icons-outlined mr-1 text-base">add</span> Nuevo préstamo
                </button>
            </div>

            {/* Inventario compartido: todos los vendedores ven qué equipo está libre y quién lo tiene */}
            <section>
                <h2 className="text-xl font-semibold text-blue-600 mb-2">
                    Equipos de préstamo <span className="text-sm text-gray-600 font-normal">({available.length} disponibles de {units.length})</span>
                </h2>
                <div className="relative overflow-x-auto shadow-lg sm:rounded-lg">
                    <table className="w-full text-sm text-left border border-gray-200">
                        <TableHeader headerOptions={['Equipo', 'Estado', 'Prospecto', 'Vendedor']} />
                        <tbody>
                            {units.length === 0 && (
                                <tr className="bg-gray-50 border-b">
                                    <td colSpan={4} className="text-center px-6 py-3">
                                        No hay equipos marcados como unidad de préstamo. El administrador los marca en la ficha del sensor.
                                    </td>
                                </tr>
                            )}
                            {units.map((u) => (
                                <tr key={u.sensorId} className="bg-gray-50 border-b text-gray-800 hover:bg-gray-100">
                                    <td className="px-6 py-3">
                                        {u.sensorId}
                                        {u.sensorName && <div className="text-xs text-gray-500">{u.sensorName}</div>}
                                    </td>
                                    <td className="px-6 py-3">
                                        {u.available && <span className="px-2 py-1 rounded text-xs font-semibold bg-green-100 text-green-800">Disponible</span>}
                                        {!u.available && u.validUntil && <span className="px-2 py-1 rounded text-xs font-semibold bg-yellow-100 text-yellow-800">Prestado hasta {formatDate(u.validUntil)}</span>}
                                        {!u.available && !u.validUntil && u.assignedToClient && <span className="px-2 py-1 rounded text-xs font-semibold bg-gray-200 text-gray-700">Asignado a un cliente</span>}
                                    </td>
                                    <td className="px-6 py-3">{u.available || !u.validUntil ? '-' : (u.prospectName || u.prospectEmail || 'Préstamo de otro vendedor')}</td>
                                    <td className="px-6 py-3 text-xs">{u.sellerEmail || '-'}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </section>

            <section>
                <div className="flex flex-wrap items-center justify-between gap-2 mb-2">
                    <h2 className="text-xl font-semibold text-blue-600">{isAdmin ? 'Todos los préstamos' : 'Mis préstamos'}</h2>
                    <label className="flex items-center gap-2 text-sm cursor-pointer">
                        <input type="checkbox" checked={showHistory} onChange={(e) => setShowHistory(e.target.checked)} />
                        Ver también los terminados
                    </label>
                </div>
                <div className="relative overflow-x-auto shadow-lg sm:rounded-lg">
                    <table className="w-full text-sm text-left border border-gray-200">
                        <TableHeader headerOptions={['Prospecto', 'Equipo', 'Estado', 'Vigencia', 'Inicio',
                            ...(isAdmin ? ['Vendedor'] : []), 'Acciones']} />
                        <tbody>
                            {visibleTrials.length === 0 && (
                                <tr className="bg-gray-50 border-b">
                                    <td colSpan={isAdmin ? 7 : 6} className="text-center px-6 py-3">No hay préstamos</td>
                                </tr>
                            )}
                            {visibleTrials.map((t) => {
                                const badge = statusBadge(t);
                                return (
                                    <tr key={t.id} className="bg-gray-50 border-b text-gray-800 hover:bg-gray-100 align-top">
                                        <td className="px-6 py-3">
                                            {t.userName || t.userEmail}
                                            {t.userName && <div className="text-xs text-gray-500">{t.userEmail}</div>}
                                        </td>
                                        <td className="px-6 py-3">{t.sensorId}</td>
                                        <td className="px-6 py-3">
                                            <span className={`px-2 py-1 rounded text-xs font-semibold whitespace-nowrap ${badge.className}`}>{badge.label}</span>
                                        </td>
                                        <td className="px-6 py-3">
                                            {validityText(t)}
                                            {isOpen(t) && <div className="text-xs text-gray-500">Hasta {formatDate(t.validUntil)}</div>}
                                        </td>
                                        <td className="px-6 py-3 whitespace-nowrap">{formatDate(t.startDate)}</td>
                                        {isAdmin && <td className="px-6 py-3 text-xs">{t.sellerEmail || '-'}</td>}
                                        <td className="px-6 py-3">
                                            {isOpen(t) ? (
                                                <div className="flex flex-wrap gap-x-3 gap-y-1">
                                                    <button type="button" className={actionLink}
                                                        onClick={() => { setExtendDays(5); setError(null); setModal({ kind: 'extend', trial: t }); }}>
                                                        Extender
                                                    </button>
                                                    <button type="button" className={actionLink}
                                                        onClick={() => { setNewPassword(''); setError(null); setModal({ kind: 'password', trial: t }); }}>
                                                        Cambiar contraseña
                                                    </button>
                                                    <button type="button" className={dangerLink} onClick={() => handleEnd(t)}>Terminar</button>
                                                </div>
                                            ) : <span className="text-gray-400 text-sm">-</span>}
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            </section>

            {modal?.kind === 'new' && (
                <ContractModal title="Nuevo préstamo" onClose={closeModal} onConfirm={handleNewLoan}
                    busy={busy} error={error} confirmLabel="Prestar equipo">
                    <div className="flex flex-col sm:flex-row gap-4">
                        <div className="flex-1">
                            <label className={labelClass} htmlFor="loanSensor">Equipo</label>
                            <select id="loanSensor" className={inputClass} required {...field('sensorId')}>
                                <option value="" disabled>Selecciona un equipo</option>
                                {available.map((u) => (
                                    <option key={u.sensorId} value={u.sensorId}>{u.sensorId}{u.sensorName ? ` · ${u.sensorName}` : ''}</option>
                                ))}
                            </select>
                        </div>
                        <div className="sm:w-32">
                            <label className={labelClass} htmlFor="loanDays">Días</label>
                            <input id="loanDays" type="number" min={1} max={maxDays} required className={inputClass} {...field('days')} />
                        </div>
                    </div>
                    {!isAdmin && <p className="text-xs text-gray-600 -mt-2">Máximo {MAX_TRIAL_DAYS} días en total, incluidas las extensiones.</p>}

                    <label className="flex items-center gap-2 text-sm cursor-pointer">
                        <input type="checkbox" checked={loan.existing} onChange={(e) => setLoan({ ...loan, existing: e.target.checked })} />
                        El prospecto ya tuvo un préstamo conmigo (solo indicar su correo)
                    </label>

                    <div>
                        <label className={labelClass} htmlFor="loanEmail">Correo del prospecto</label>
                        <input id="loanEmail" type="email" required className={inputClass} {...field('email')} />
                    </div>

                    {!loan.existing && (
                        <>
                            <div className="flex flex-col sm:flex-row gap-4">
                                <div className="flex-1">
                                    <label className={labelClass} htmlFor="loanFirstName">Nombres</label>
                                    <input id="loanFirstName" required minLength={2} className={inputClass} {...field('firstName')} />
                                </div>
                                <div className="flex-1">
                                    <label className={labelClass} htmlFor="loanLastName">Apellidos</label>
                                    <input id="loanLastName" required minLength={2} className={inputClass} {...field('lastName')} />
                                </div>
                            </div>
                            <div className="flex flex-col sm:flex-row gap-4">
                                <div className="flex-1">
                                    <label className={labelClass} htmlFor="loanPhone">Teléfono</label>
                                    <input id="loanPhone" required pattern="^\+?[1-9]\d{1,14}$" title="Solo números"
                                        className={inputClass} {...field('phone')} />
                                </div>
                                <div className="flex-1">
                                    <label className={labelClass} htmlFor="loanTz">Zona horaria</label>
                                    <select id="loanTz" required className={inputClass} {...field('timeZoneId')}>
                                        <option value="" disabled>Selecciona</option>
                                        {timeZones.map((tz) => <option key={tz.id} value={tz.id}>{tz.name} (UTC{tz.timeOffset})</option>)}
                                    </select>
                                </div>
                            </div>
                            <div>
                                <label className={labelClass} htmlFor="loanAddress">Dirección / finca</label>
                                <input id="loanAddress" required className={inputClass} {...field('address')} />
                            </div>
                            <div>
                                <label className={labelClass} htmlFor="loanPlan">Vista de la plataforma</label>
                                <select id="loanPlan" className={inputClass} {...field('plan')}>
                                    <option value="FULL">Completa (alquiler): incluye Mis cultivos</option>
                                    <option value="BASIC">Básica (compra)</option>
                                </select>
                                <p className="text-xs text-gray-600 mt-1">
                                    Lo que verá el prospecto durante la prueba. Si luego contrata otra modalidad, el admin ajusta su rol.
                                </p>
                            </div>
                            <div>
                                <label className={labelClass} htmlFor="loanPassword">Contraseña para el prospecto</label>
                                <input id="loanPassword" type="text" required autoComplete="off" className={inputClass} {...field('password')} />
                                <p className="text-xs text-gray-600 mt-1">{PASSWORD_HINT}</p>
                            </div>
                        </>
                    )}

                    <div>
                        <label className={labelClass} htmlFor="loanNotes">Notas</label>
                        <input id="loanNotes" className={inputClass} placeholder="Cultivo, ubicación, acuerdos..." {...field('notes')} />
                    </div>
                </ContractModal>
            )}

            {modal?.kind === 'extend' && (
                <ContractModal title={`Extender préstamo · ${modal.trial.sensorId}`} onClose={closeModal} busy={busy} error={error}
                    confirmLabel="Extender"
                    onConfirm={() => submit(() => extendTrial(modal.trial.id, Number(extendDays)), 'Préstamo extendido.')}>
                    <p className="text-sm text-gray-700">
                        Vence el <b>{formatDate(modal.trial.validUntil)}</b>.
                        {!isAdmin && <> Como máximo puede llegar al <b>{formatDate(addDays(modal.trial.startDate, MAX_TRIAL_DAYS))}</b>.</>}
                    </p>
                    <div>
                        <label className={labelClass} htmlFor="extendDays">Días adicionales</label>
                        <input id="extendDays" type="number" min={1} required className={inputClass}
                            value={extendDays} onChange={(e) => setExtendDays(e.target.value)} />
                        <p className="text-xs text-gray-600 mt-1">
                            Nuevo vencimiento: {extendDays > 0 ? formatDate(addDays(modal.trial.validUntil, Number(extendDays))) : '-'}
                        </p>
                    </div>
                </ContractModal>
            )}

            {modal?.kind === 'password' && (
                <ContractModal title={`Cambiar contraseña · ${modal.trial.userEmail}`} onClose={closeModal} busy={busy} error={error}
                    confirmLabel="Cambiar contraseña"
                    onConfirm={() => submit(() => changeProspectPassword(modal.trial.userEmail, newPassword), 'Contraseña actualizada.')}>
                    <div>
                        <label className={labelClass} htmlFor="newPassword">Nueva contraseña</label>
                        <input id="newPassword" type="text" required autoComplete="off" className={inputClass}
                            value={newPassword} onChange={(e) => setNewPassword(e.target.value)} />
                        <p className="text-xs text-gray-600 mt-1">{PASSWORD_HINT}</p>
                    </div>
                </ContractModal>
            )}
        </div>
    );
};

export default LoansView;
